import React, { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Species } from '../../types/gql-op-types';
import styles from './EscapeAlert.module.scss';

const CLIPS = {
  [Species.DOG]: {
    video: '/product_images/rescue/escape-alert-dog.mp4',
    poster: '/product_images/rescue/escape-alert-dog-poster.jpg',
  },
  [Species.CAT]: {
    video: '/product_images/rescue/escape-alert-cat.mp4',
    poster: '/product_images/rescue/escape-alert-cat-poster.jpg',
  },
};

/**
 * Seconds into the clip at which the pet crosses the safe zone edge, and the frame the Nano offer
 * email's GIF drops its notification in.
 */
const ALERT_AT_SECONDS = 2.2;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Plays the clip while it is on screen and reports whether the notification should show, read off the
 * video's own clock so it lands on the same frame every loop. Safe under Strict Mode: each run sets up
 * its own observer and frame loop and the cleanup tears both down.
 */
function useEscapeClip(videoRef: RefObject<HTMLVideoElement>, reducedMotion: boolean) {
  const [alertShown, setAlertShown] = useState(reducedMotion);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) {
      return;
    }

    // muted on the node rather than as a prop: react never writes the `muted` attribute, and ios
    // refuses to autoplay a clip that is not muted
    video.muted = true;

    let cancelled = false;
    let frame = 0;
    const followClip = () => {
      if (!video.paused) {
        setAlertShown(video.currentTime >= ALERT_AT_SECONDS);
      }
      frame = requestAnimationFrame(followClip);
    };

    const start = () => {
      // older browsers (and jsdom) return nothing from play() rather than a promise
      const started: Promise<void> | undefined = video.play();
      // autoplay refused (low power mode): the poster is the last frame, so show the alert to match
      started?.catch(() => !cancelled && setAlertShown(true));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(followClip);
    };

    const stop = () => {
      video.pause();
      cancelAnimationFrame(frame);
    };

    // plays only while on screen: most of the visit is spent typing in the checkout below it
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
      };
    }
    const observer = new IntersectionObserver(([entry]) => (entry?.isIntersecting ? start() : stop()));
    observer.observe(video);

    return () => {
      cancelled = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [videoRef, reducedMotion]);

  return alertShown;
}

interface EscapeAlertProps {
  species: Species;
  /** What the clip shows, for screen readers. */
  label: string;
  /** The notification drawn over the clip, named for the adopter's pet where the link names it. */
  title: string;
  body: string;
  /** Set beside the clip on desktop and under it on a phone. */
  children?: ReactNode;
}

/**
 * The escape-alert clip from the Nano offer emails and the Nano checkout: the pet walks out of its
 * safe zone and the phone shows "{name} left Home". The clip has the notification painted out and it
 * is drawn here in markup, so the alert names the adopter's own pet rather than the email's sample one.
 *
 * With reduced motion the clip stays on its last frame, the pet outside the zone and the alert shown.
 */
export default function EscapeAlert({ species, label, title, body, children }: EscapeAlertProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion] = useState(prefersReducedMotion);
  const alertShown = useEscapeClip(videoRef, reducedMotion);
  const clip = species === Species.CAT ? CLIPS[Species.CAT] : CLIPS[Species.DOG];

  return (
    <div className={styles.frame}>
      <div className={styles.screen} role="img" aria-label={label}>
        <video
          ref={videoRef}
          className={styles.video}
          src={reducedMotion ? undefined : clip.video}
          poster={clip.poster}
          loop
          playsInline
          preload={reducedMotion ? 'none' : 'auto'}
          aria-hidden="true"
        />
        <div className={alertShown ? `${styles.alert} ${styles.alertShown}` : styles.alert} aria-hidden="true">
          <span className={styles.alertIcon}>{t('rescue_checkout_notification_icon')}</span>
          <span className={styles.alertText}>
            <span className={styles.alertTitleRow}>
              <span className={styles.alertTitle}>{title}</span>
              <span className={styles.alertTime}>{t('rescue_checkout_notification_time')}</span>
            </span>
            <span className={styles.alertBody}>{body}</span>
          </span>
        </div>
      </div>
      {children && <div className={styles.caption}>{children}</div>}
    </div>
  );
}
